import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface Member {
  userId: string;
  displayName: string;
  eloRating: number;
  role: string;
}

interface TodayPuzzle {
  index: number;
  puzzleId: string;
  puzzleNumber: number;
  completed: boolean;
  score: number | null;
  won: boolean;
}

interface LeaderEntry {
  rank: number;
  displayName: string;
  totalScore: number;
  puzzlesCompleted: number;
  isMe: boolean;
}

export function LeagueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [puzzles, setPuzzles] = useState<TodayPuzzle[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderEntry[]>([]);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    const [detail, today, lb] = await Promise.all([
      api<{ league: { name: string; inviteCode?: string }; members: Member[] }>(`/leagues/${id}`),
      api<{ puzzles: TodayPuzzle[] }>(`/leagues/${id}/today`),
      api<{ entries: LeaderEntry[] }>(`/leagues/${id}/leaderboard`),
    ]);
    setName(detail.league.name);
    setInviteCode(detail.league.inviteCode ?? null);
    setMembers(detail.members);
    setPuzzles(today.puzzles);
    setLeaderboard(lb.entries);
  }, [id]);

  useEffect(() => {
    if (!user) return;
    load().catch(() => navigate('/leagues'));
  }, [user, load, navigate]);

  const copyInvite = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent */
    }
  };

  if (!user || !id) return null;

  return (
    <main className="page leagues">
      <div className="profile-card">
        <Link to="/leagues" className="link-muted">← {t('leagues.title')}</Link>
        <h1>{name}</h1>

        {inviteCode && (
          <div className="league-invite">
            <p>{t('leagues.shareCode')}</p>
            <code className="invite-code">{inviteCode}</code>
            <button type="button" className="btn secondary" onClick={copyInvite}>
              {copied ? t('result.copied') : t('leagues.copy')}
            </button>
          </div>
        )}

        <section className="friends-section">
          <h2>{t('leagues.today')}</h2>
          <div className="league-puzzle-grid">
            {puzzles.map((p) => (
              <Link
                key={p.index}
                to={p.completed ? '#' : `/leagues/${id}/play/${p.index}`}
                className={`league-puzzle-slot${p.completed ? ' done' : ''}`}
                onClick={(e) => p.completed && e.preventDefault()}
              >
                <span className="slot-num">#{p.index}</span>
                {p.completed ? (
                  <span className="slot-score">{p.score ?? 0} pt</span>
                ) : (
                  <span className="slot-play">{t('leagues.play')}</span>
                )}
              </Link>
            ))}
          </div>
        </section>

        <section className="friends-section">
          <h2>{t('leagues.leaderboard')}</h2>
          <ol className="lb-list">
            {leaderboard.map((e) => (
              <li key={e.rank} className={e.isMe ? 'me' : ''}>
                <span className="rank">#{e.rank}</span>
                <span className="name">{e.displayName}</span>
                <span className="pts">{e.totalScore}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="friends-section">
          <h2>{t('leagues.members')}</h2>
          <ul className="friends-list muted">
            {members.map((m) => (
              <li key={m.userId}>
                {m.displayName} {m.role === 'owner' ? '★' : ''} · Elo {m.eloRating}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
